import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { HeaderService } from '../../../../services/header/header.service';
import { ExportService } from '../../shared/export.service';
import { ToastrService } from 'ngx-toastr';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { readObservableError } from 'app/services/logiciel-caisse/logiciel-caisse-api.helper';
import { LogicielCaisseTicketSelectionService } from 'app/services/logiciel-caisse/logiciel-caisse-ticket-selection.service';
import {
  LogicielCaisseTicketService,
} from 'app/services/logiciel-caisse/logiciel-caisse-ticket.service';
import { LogicielCaisseSocketService } from 'app/services/logiciel-caisse/logiciel-caisse-socket.service';
import { TicketFactureErpService } from 'app/services/document/ticket-facture-erp.service';
import {
  ModalFinancialProduct,
  TicketBatchProceedPayload,
} from '../facturer-multi-ticket-modal/facturer-multi-ticket-modal.component';
import { ProductsService } from 'app/services/products/products.service';
import { getEnterpriseStationIdFromUser } from 'app/services/enterprise-context';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ticket-caisse',
  templateUrl: './ticket-caisse.component.html',
})
export class TicketCaisseComponent implements OnInit, OnDestroy {
    currentStatus = 'tous';

    userId: number | null = null;
    stationId: number | null = null;
    selectedCount = 0;
    showAnnulerModal = false;
    cancelSubmitting = false;
    showFacturerModal = false;
    factureSubmitting = false;
    facturerModalTicketIds: number[] = [];
    financialProducts: ModalFinancialProduct[] = [];
    /** Snapshot figé à l’ouverture du modal (évite rechargement en boucle). */
    annulerModalTicketIds: number[] = [];
    isAdmin = true;

    private selectionSub?: Subscription;
    private socketSub?: Subscription;
    private navigationSub?: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private headerService: HeaderService,
        private exportService: ExportService,
        private toastr: ToastrService,
        private authService: LogicielCaisseAuthService,
        private selectionService: LogicielCaisseTicketSelectionService,
        private ticketService: LogicielCaisseTicketService,
        private socketService: LogicielCaisseSocketService,
        private readonly ticketFactureErp: TicketFactureErpService,
        private readonly productsService: ProductsService,
        private readonly cdr: ChangeDetectorRef
    ) {
        const session = this.authService.getSession();
        this.userId = session?.userId ?? null;
        this.stationId = this.authService.getBackStationId();
        // Logiciel-caisse : utilisateur connecté = propriétaire de la station (administration)
        this.isAdmin = true;
    }

    ngOnInit(): void {
        this.syncCurrentStatusFromRoute();
        this.applyHeaderConfig();
        this.selectionSub = this.selectionService.selectedIds$.subscribe((ids) => {
            this.selectedCount = ids.size;
            this.applyHeaderConfig();
        });
        this.navigationSub = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.syncCurrentStatusFromRoute();
                this.applyHeaderConfig();
            }
        });
        if (this.authService.isAuthenticated()) {
            this.refreshSessionIds();
            this.socketService.connect();
        }
        this.socketSub = this.socketService.ticketUpdated$.subscribe((payload) => {
            this.selectionService.requestSocketTicketUpdate(payload);
        });
    }

    private refreshSessionIds(): void {
        const session = this.authService.getSession();
        this.userId = session?.userId ?? null;
        this.stationId = this.authService.getBackStationId();
    }

    /** Onglet actif (tous, ticke, consolide, …) depuis la route enfant. */
    private syncCurrentStatusFromRoute(): void {
        const urlPath = this.router.url.split('?')[0];
        const routeMatch = urlPath.match(/\/tiers\/ticketCaisse\/([^/]+)/i);
        if (routeMatch?.[1]) {
            this.currentStatus = routeMatch[1].toLowerCase();
            return;
        }
        const segs = this.route.firstChild?.snapshot.url ?? [];
        const path = segs.length > 0 ? segs[0].path : 'tous';
        this.currentStatus = path.toLowerCase();
    }

    selectInternalStatus(status: 'ticke' | 'consolide'): void {
        this.currentStatus = status;
        this.applyHeaderConfig();
    }

    ngOnDestroy(): void {
        this.selectionSub?.unsubscribe();
        this.socketSub?.unsubscribe();
        this.navigationSub?.unsubscribe();
        // Ne pas disconnect() le socket partagé : cycle de vie = authStatus$ du service.
    }

    openAnnulerModal(): void {
        if (this.selectedCount === 0) {
            return;
        }
        this.annulerModalTicketIds = [...this.selectionService.getSelectedIds()];
        this.showAnnulerModal = true;
    }

    closeAnnulerModal(): void {
        if (!this.cancelSubmitting) {
            this.showAnnulerModal = false;
        }
    }

    openFacturerModal(): void {
        if (this.selectedCount === 0) {
            return;
        }
        this.facturerModalTicketIds = [...this.selectionService.getSelectedIds()];
        this.showFacturerModal = true;
        this.loadFinancialProducts();
    }

    /** Produits financiers ERP (Recette + autres) proposés à l'étape « Produits financiers ». */
    private loadFinancialProducts(): void {
        const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
        const erpStationId = getEnterpriseStationIdFromUser(currentUser);
        const erpClientId = Number(currentUser?.id);
        if (!erpStationId || !erpClientId) {
            this.financialProducts = [];
            return;
        }
        // On garantit « Recette » : la liste des produits financiers n'est jamais vide.
        this.productsService.getFinancialProductsEnsuringRecette(erpStationId, erpClientId).subscribe({
            next: (list) => {
                this.financialProducts = (list || []).map((p) => ({
                    id: Number((p as { id: number }).id),
                    nom: (p as { nom?: string }).nom,
                    ref_interne: (p as { ref_interne?: string }).ref_interne,
                    tva_id: (p as { tva_id?: number | null }).tva_id ?? null,
                }));
            },
            error: () => {
                this.financialProducts = [];
            },
        });
    }

    closeFacturerModal(): void {
        if (!this.factureSubmitting) {
            this.showFacturerModal = false;
        }
    }

    onProceedFacturer(event: TicketBatchProceedPayload): void {
        if (!event.ticketIds.length) {
            return;
        }
        this.factureSubmitting = true;
        // Transfert réservé aux tickés : après création / approbation facture → consolidé.
        const markConsolide = true;
        const allowConsolide = false;
        const done = () => {
            this.factureSubmitting = false;
            this.showFacturerModal = false;
            this.selectionService.clear();
            // Tickets facturés → consolidés : hors Tické / Tous, visibles seulement en Consolidé.
            this.selectionService.requestListRefresh();
            this.selectionService.requestCountsRefresh();
        };
        const onError = (err: unknown) => {
            this.toastr.error(
                readObservableError(err, 'Ouverture de la facture ERP impossible.'),
                'Erreur'
            );
            this.factureSubmitting = false;
        };

        // Établissement (filtre POS) ≠ entrepôt (issu des tickets).
        const etab = this.selectionService.getSelectedEstablishment();
        const etablissementOpts = {
            etablissementId: etab?.id ?? null,
            etablissementLibelle: etab?.label ?? null,
            etablissementCode: etab?.code ?? null,
        };
        const entrepotId =
            event.tickets.map((t) => t.entrepotId).find((id) => id != null && Number(id) > 0) ?? null;

        if (event.mode === 'product_lines') {
            this.ticketFactureErp
                .openErpFactureFromTicketsWithProductLines(event.ticketIds, {
                    markConsolide,
                    allowConsolide,
                    entrepotId,
                    ...etablissementOpts,
                })
                .subscribe({ next: done, error: onError });
            return;
        }

        const financialProduct =
            event.financialProductId != null
                ? this.financialProducts.find((p) => p.id === event.financialProductId) ?? null
                : null;
        this.ticketFactureErp
            .openErpFactureFromTickets(event.ticketIds, {
                markConsolide,
                allowConsolide,
                tickets: event.tickets,
                financialProduct,
                entrepotId,
                ...etablissementOpts,
            })
            .subscribe({ next: done, error: onError });
    }

    onConfirmAnnuler(event: { ticketIds: number[]; allowPaidCancel: boolean }): void {
        if (!event.ticketIds.length) {
            return;
        }
        this.cancelSubmitting = true;
        this.ticketService
            .cancelTicketsBatch(event.ticketIds, {
                allowPaidCancel: event.allowPaidCancel,
                stationId: this.stationId ?? undefined,
            })
            .subscribe({
                next: (res) => {
                    const n = res.cancelled?.length ?? event.ticketIds.length;
                    this.toastr.success(`${n} ticket(s) annulé(s).`, 'Annulation');
                    this.cancelSubmitting = false;
                    this.showAnnulerModal = false;
                    this.selectionService.clear();
                    this.selectionService.requestListRefresh();
                },
                error: (err: Error) => {
                    this.toastr.error(err?.message || 'Annulation impossible.', 'Erreur');
                    this.cancelSubmitting = false;
                },
            });
    }

    private applyHeaderConfig(): void {
        const buttons: {
            label: string;
            type: 'primary' | 'secondary' | 'dark' | 'dropdown';
            action?: () => void;
            dropdownItems?: { label: string; action: () => void; icon?: string }[];
        }[] = [];

        if (this.selectedCount > 0) {
            buttons.push({
                label: `Annuler (${this.selectedCount})`,
                type: 'secondary',
                action: () => this.openAnnulerModal(),
            });
            if (this.canShowFactureBatchAction()) {
                buttons.push({
                    label: `Transférer à facture (${this.selectedCount})`,
                    type: 'primary',
                    action: () => this.openFacturerModal(),
                });
            }
        }

        buttons.push({
            label: 'Exporter',
            type: 'dropdown',
            dropdownItems: [
                {
                    label: 'PDF',
                    action: () => this.exportService.triggerPDF(),
                    icon: '../../../assets/images/logo/pdf_logo.png',
                },
                {
                    label: 'CSV',
                    action: () => this.exportToCSV(),
                    icon: '../../../assets/images/logo/csv_logo.png',
                },
            ],
        });

        this.headerService.setConfig({
            title: 'Ticket caisse',
            buttons,
        });
    }

    private exportToCSV(): void {
        this.toastr.info('Export CSV à brancher sur les tickets sélectionnés.', 'Tickets');
    }

    /** Multi-sélection : transfert vers facture uniquement depuis l’onglet Tické. */
    private canShowFactureBatchAction(): boolean {
        return this.currentStatus === 'ticke';
    }
}
